import { useQuery } from '@tanstack/react-query'
import { Brain, CircleDot, DollarSign, Gauge as GaugeIcon } from 'lucide-react'
import { convictionApi } from '../api/client'
import { relativeTime } from '../lib/relativeTime'
import { THETA_MAX, THETA_MIN } from '../lib/theta'
import './ConvictionStrip.css'

/**
 * Hero strip summarizing the conviction engine's global/research-scope
 * state: on/off chips, a theta dial, an LLM budget gauge, and tick
 * freshness. Degrades to an "awaiting takeover deploy" empty state on
 * error or when the engine hasn't been switched on yet.
 */
export function ConvictionStrip() {
  const { data, isError, isLoading } = useQuery({
    queryKey: ['conviction-status', 'fleet'],
    queryFn: () => convictionApi.status(),
    retry: 1,
    refetchInterval: 30000,
  })

  const showEmpty = !isLoading && (isError || !data)

  const theta = data?.theta ?? 0
  const thetaPct = Math.min(100, Math.max(0, ((theta - THETA_MIN) / (THETA_MAX - THETA_MIN)) * 100))

  const budgetTotal = data?.llm_daily_budget_usd ?? 0
  const budgetRemaining = data?.llm_budget_remaining_usd ?? null
  const budgetPct =
    budgetTotal > 0 && budgetRemaining !== null
      ? Math.min(100, Math.max(0, (budgetRemaining / budgetTotal) * 100))
      : 0
  const lastTickAt =
    data?.last_tick && typeof data.last_tick === 'object' && data.last_tick !== null
      ? (data.last_tick as Record<string, unknown>)['as_of'] ?? (data.last_tick as Record<string, unknown>)['timestamp']
      : null

  return (
    <div className="card conviction-strip">
      <div className="card-header">
        <h3 className="card-title">
          <Brain size={18} className="icon" aria-hidden="true" />
          Conviction engine
        </h3>
      </div>

      {showEmpty ? (
        <div className="empty-state conviction-strip-empty">
          <div className="empty-icon"><Brain size={28} aria-hidden="true" /></div>
          <div className="empty-text">conviction engine — awaiting takeover deploy</div>
        </div>
      ) : (
        <div className="conviction-strip-body">
          <div className="conviction-chips">
            <span className={`conviction-chip ${data?.provider_enabled ? 'on' : 'off'}`}>
              <CircleDot size={11} aria-hidden="true" />
              Provider {data?.provider_enabled ? 'on' : 'off'}
            </span>
            <span className={`conviction-chip ${data?.entries_enabled ? 'on' : 'off'}`}>
              <CircleDot size={11} aria-hidden="true" />
              Entries {data?.entries_enabled ? 'on' : 'off'}
            </span>
          </div>

          <div className="conviction-metric">
            <div className="conviction-metric-header">
              <span className="conviction-metric-label">
                <GaugeIcon size={13} aria-hidden="true" />
                θ thesis valve
              </span>
              <span className="conviction-metric-value num">{theta.toFixed(2)}</span>
            </div>
            <div className="conviction-dial">
              <div className="conviction-dial-track">
                <div className="conviction-dial-marker" style={{ left: `${thetaPct}%` }} />
              </div>
              <div className="conviction-dial-bounds">
                <span>{THETA_MIN.toFixed(2)}</span>
                <span>{THETA_MAX.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div
            className="conviction-metric"
            title="Daily cap for dossier LLM scoring. Spend occurs on discovery days (first trading day of the month); the counter resets at 00:00 UTC."
          >
            <div className="conviction-metric-header">
              <span className="conviction-metric-label">
                <DollarSign size={13} aria-hidden="true" />
                LLM budget
              </span>
              {/* "spent today", never "remaining / total" — a full budget in
                  the remaining/total format reads as exhausted (see
                  DossierABCard). The gauge below still tracks remaining. */}
              <span className="conviction-metric-value num">
                {budgetRemaining !== null
                  ? `$${Math.max(0, budgetTotal - budgetRemaining).toFixed(2)} spent today`
                  : '— spent today'} · ${budgetTotal.toFixed(2)} cap
              </span>
            </div>
            <div className="gauge-track conviction-budget-track">
              <div
                className={`gauge-fill conviction-budget-fill ${budgetPct < 15 ? 'danger' : budgetPct < 40 ? 'warning' : 'success'}`}
                style={{ width: `${budgetPct}%` }}
              />
            </div>
          </div>

          <div className="conviction-strip-footer">
            Last tick <span className="num">{relativeTime(typeof lastTickAt === 'string' ? lastTickAt : null, 'no tick yet')}</span>
          </div>
        </div>
      )}
    </div>
  )
}
